import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "react-query";

import { selectTxnId, useMapStore } from "stores/map";

import {
  ENTITIES_ON_MAP_QUERY_KEY,
  ENTITIES_ON_MAP_STALE_TIME,
  MAP_QUERY_CACHE_TIME,
  fetchEntitiesOnMap
} from "api/map";

import { updateColorMap, useTable } from "components/data-table/context";
import useShouldFetchEntitiesOnMap from "components/map/hooks/useShouldFetchEntitiesOnMap";

import { EntityKind } from "../../../models/entityKind";
import {
  EntitiesInGroupModelSchemaType,
  EntitiesOnMapResponseSchema,
  PartialTxnTSchema
} from "../../../models/schemas";

/**
 * safely extracts the transaction ID based on entity kind
 */
const useSafeTxnId = (entityKind: EntityKind) => {
  const rawTxnId = useMapStore(selectTxnId);

  return useMemo(() => {
    const parseResult = PartialTxnTSchema.safeParse(rawTxnId);

    if (!parseResult.success) {
      Sentry.captureException(parseResult.error, {
        extra: { context: "useSafeTxnId - Invalid txnId shape", rawTxnId }
      });
      return undefined;
    }

    const txnId = parseResult.data;
    const id = entityKind === EntityKind.Well ? txnId?.id : txnId?.facilityId;

    // Return undefined if id is empty string or falsy (null/undefined)
    // This ensures the query is only enabled when there's a valid non-empty ID
    return id && id !== "" ? id : undefined;
  }, [rawTxnId, entityKind]);
};

/**
 * Simple `hook` that
 * - fetches entities on Map,
 * - extracts uwi: #hexcode pair, and
 * - saves it to TableContext as `colorMap`
 */
const useEntitiesOnMap = () => {
  const [{ entityKind }, dispatch] = useTable();
  const currentTxnId = useSafeTxnId(entityKind);
  const shouldFetchEntitiesOnMap = useShouldFetchEntitiesOnMap();

  const queryFn = useCallback(() => fetchEntitiesOnMap(currentTxnId), [currentTxnId]);

  const { data, isSuccess } = useQuery(
    [ENTITIES_ON_MAP_QUERY_KEY, currentTxnId],
    queryFn,
    {
      enabled: !!currentTxnId && shouldFetchEntitiesOnMap,
      retry: false,
      staleTime: ENTITIES_ON_MAP_STALE_TIME,
      cacheTime: MAP_QUERY_CACHE_TIME
    }
  );

  useEffect(() => {
    if (!currentTxnId || !shouldFetchEntitiesOnMap) {
      updateColorMap(dispatch, {});
      return;
    }

    // Skip if query hasn't succeeded or no data
    if (!isSuccess || !data) return;

    const parseResult = EntitiesOnMapResponseSchema.safeParse(data);

    if (!parseResult.success) {
      // Validation failed but proceed anyway to avoid blocking table rendering
      // Log to Sentry for debugging the schema mismatch
      Sentry.captureException(parseResult.error, {
        extra: {
          context: "useEntitiesOnMap - Invalid API response shape (proceeding anyway)",
          data
        }
      });
    }

    // Extract {uwi: #hexCode} pairs from all groups
    // Use raw data if validation failed, validated data if it succeeded
    const sourceData = parseResult.success ? parseResult.data : data;
    const nextValue = Object.values(sourceData).reduce<Record<string, string>>(
      (acc, group: EntitiesInGroupModelSchemaType) => {
        // Handle both validated and raw data structures
        Object.assign(acc, group?.uwis || {});
        return acc;
      },
      {}
    );

    updateColorMap(dispatch, nextValue);
  }, [isSuccess, data, dispatch, currentTxnId, shouldFetchEntitiesOnMap]);
};

export default useEntitiesOnMap;
